<template>
  <div class="sentiment-trends-chart">  
    <div class="chart" ref="chart"></div>
  </div>
</template>
<script>
import echarts from 'echarts';
import QuestionQuery from '@/rest/questionQuery';
import funcHelper from '@/helpers/funcHelper';

export default {
  props: {
    searchText: {
      type: String
    }
  },
  data() {
    return {
      chart: null,
      chartData: null,
      dateRangeArr: [],
      indexArrData: ''
    };
  },
  methods: {
    getXAxisData() {
      const data = [];
      const dateNow = +new Date();
      const msOfDay = 24 * 60 * 60 * 1000; // milliseconds of a day
      const daysOfYear = 365;
      for (let index = 1; index <= daysOfYear; ++index) {
        data.push(dateNow - (daysOfYear - index) * msOfDay);
      }
      return data.map(item => {
        const datetime = new Date(item);
        const year = datetime.getFullYear();
        const month = (datetime.getMonth() + 1).toString().padStart(2, '0');
        const date = datetime.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${date}`;
      }).filter(item => !/2018/.test(item)); // filter out 2018 year
    },
    getIndexSeriesData(dateRange, chartData, type) { // type: hotIndex, positiveIndex, negativeIndex
      return dateRange.map(date => {
        const data = chartData[type].filter(item => item.date === date)[0];
        if (data) {
          return data.value;
        }
        return 0;
      });
    },
    getSeriesData(dateRange, chartData) {
      // const numberSeriesData = this.getIndexSeriesData(dateRange, chartData, 'number');
      const sentimentSeriesData = this.getIndexSeriesData(dateRange, chartData, 'sentiment');
      const events = chartData.events || [];
      const eventSeriesData = events.map(item => {
        return [item.displayAt, item.displayLabelHeight, item.name, item.id];
      });
      const eventLines = events.map((item) => {
        return {
          data: [[item.displayAt, item.index], [item.displayAt, item.displayLabelHeight, 'end']],
          name: item.name
        };
      });
      // 处理echarts关于显示多标签的bug, 并不清楚原理，只知道这样可以避免
      eventLines.push({
        data: [[dateRange[0], 0], [dateRange[0], 0, 'pad']],
        name: 'pad'
      });

      const series = [          
        {
          type: 'line',
          name: '舆情指数',
          data: sentimentSeriesData,
          smooth: true,
          symbol: 'circle',
          symbolSize: 0,
          emphasis: {
            itemStyle: {
              borderWidth: 1
            }
          },
          lineStyle: {
            color: '#45a8cc'
          }
        },
        // {
        //   type: 'line',
        //   name: '新闻热度',
        //   data: numberSeriesData,
        //   yAxisIndex: 0,
        //   smooth: true,
        //   symbol: 'circle',
        //   symbolSize: 4,
        //   showSymbol: false,
        //   emphasis: {
        //     itemStyle: {
        //       color: 'white',
        //       borderWidth: 2
        //     }
        //   },
        //   lineStyle: {
        //     color: '#ff9f5f'
        //   }
        // },
        {
          data: eventSeriesData,
          type: 'custom',
          renderItem: (params, api) => {
            const point = api.coord([api.value(0), api.value(1)]);
            const origialText = api.value(2);
            const width = 130;
            
            const zhCharWidth = 12;
            const enCharWidth = 7;
            const textPos = {
              x: 4,
              y: 8
            };

            const textMaxWidth = width - 2 * textPos.x;

            const result = [];
            let lineCount = 1;
            const enCharReg = /[a-zA-Z0-9,.\s]/;
            let rowLength = 0;
            for (let index = 0; index < origialText.length; ++index) { 
              const item = origialText[index];
              let charLength = 0;             
              if (enCharReg.test(item)) { // en char
                charLength = enCharWidth;
              } else {
                charLength = zhCharWidth;
              }
              rowLength += charLength;
              if (rowLength > textMaxWidth) { // roll back
                result.push(`\n${item}`);
                lineCount++;
                rowLength = charLength; // reset row length to char length
              } else {
                result.push(item);
              }
            }

            const height = lineCount * 12 + 16;
            return {
              type: 'group',
              z2: 100,
              position: [point[0] - width / 2, point[1] - height],
              width,
              height,
              children: [
                {
                  type: 'rect',
                  z2: 100,
                  shape: {
                    x: 0,
                    y: 0,
                    width,
                    height,
                    r: 2
                  },
                  style: {
                    fill: '#33404c',
                    lineWidth: 1,
                    stroke: 'rgba(64, 204, 255, 0.2)'
                  },
                  styleEmphasis: {
                    fill: '#357089',
                    lineWidth: 1,
                    stroke: 'rgba(64, 204, 255, 0.2)'
                  }
                },
                {
                  type: 'text',
                  z2: 100,
                  position: [textPos.x, textPos.y],
                  style: {
                    fill: 'rgba(255, 255, 255, 0.8)',
                    text: result.join(''),
                    textVerticalAlgin: 'middle'
                  }
                }
              ]
            };
          }
        }
      ];
      
      eventLines.forEach(item => {
        series.push({
          type: 'line',
          symbolSize: 1,
          data: item.data,
          yAxisIndex: 0,
          lineStyle: {
            type: 'dotted',
            color: '#3b4e63'
          }
        });
      });
      return series;
    },
    getChartOptionsWithoutSeries(dateRange) {  
      const options = {
        animation: false,
        grid: {
          left: 60,
          right: 60
        },
        legend: {
          z: 1,
          backgroundColor: 'transparent',
          orient: 'horizontal',
          top: 'top',
          right: '10',
          shadowBlur: 0,
          textStyle: {
            color: '#464d6c',
            fontSize: 14,
            padding: [8, 0]
          },
          icon: 'roundRect'
        },
        tooltip: {
          show: true,
          backgroundColor: 'transparent',
          formatter: (params) => {
            if (Array.isArray(params)) {
              const indexArr = params.slice(0, 3);
              this.indexArrData = indexArr;
              return `<div style="padding: 16px; border: solid 1px #f7f7fa; background-color: #ffffff;  box-shadow: 0px 6px 10px 0px rgba(10, 31, 68, 0.1);"><div style="font-size: 12px;color: #464d6c;">
                ${indexArr[0].name}
                </div>
                ${indexArr.map(v => `<div style="color:#171f46;">${v.seriesName}: <span style="color:${v.color};">${v.data}</span></div>`).join('')}</div>`;
            }
            return '';
          }
        },
        color: ['#45a8cc', '#ff9f5f'],
        dataZoom: {
          type: 'slider', 
          backgroundColor: '#ffffff',
          fillerColor: 'rgba(23, 31, 70, 0.09)',
          borderColor: '#f7f7fa',
          bottom: 0,
          handleIcon: 'image:///handle.svg',
          handleSize: '90%',
          dataBackground: {
            lineStyle: {
              color: 'rgba(23,31,70,0)'
            },
            areaStyle: {
              color: 'rgba(23,31,70,.09)'
            }
          } 
        },
        xAxis: [
          {
            type: 'category',
            data: dateRange,
            nameTextStyle: {
              color: '#747990'
            },
            axisPointer: {
              show: true,
              type: 'line',
              label: {
                margin: 4,
                backgroundColor: '#ffffff',
                borderColor: 'rgba(209,210,218)',
                color: '#464D6C'
              },
              lineStyle: {
                color: '#45A8CC'
              }
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: 'rgba(247,247,250,1)'
              }
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: '#464d6c'
            }
          }
        ],
        yAxis: [
          {
            type: 'value',
            show: true,
            nameTextStyle: {
              color: '#747990'
            },
            axisLabel: {
              color: '#747990',
              fontSize: 12
            },
            axisTick: {
              show: false
            },
            axisLine: {
              lineStyle: {
                color: '#f7f7fa'
              }
            },
            splitLine: {
              show: true,
              lineStyle: {
                // 使用深浅的间隔色
                color: '#f7f7fa'
              }
            }
          }
        ]
      };
      return options;
    },
    renderChart(chartData) {  
      const dataRequestDebounce = funcHelper.debounce((ticker, startDate, endDate) => {
        QuestionQuery.getSentimentTrends(ticker, startDate, endDate).then(data => {
          this.chartData.events = data.events || [];
          this.renderChart(this.chartData);
        });
      }, 300);

      if (!this.chart) {
        this.chart = echarts.init(this.$refs.chart); 
        const chartOption = this.getChartOptionsWithoutSeries(this.dateRangeArr);
        const chartSeries = this.getSeriesData(this.dateRangeArr, chartData);
        chartOption.series = chartSeries || [];
        this.chart.setOption(chartOption);
        this.chart.on('dataZoom', (evt) => {
          const daysOfYear = this.dateRangeArr.length;
          const startDate = this.dateRangeArr[Math.floor(daysOfYear * evt.start / 100)];
          const endDate = this.dateRangeArr[Math.floor(daysOfYear * evt.end / 100) - 1];
          dataRequestDebounce(this.searchText, startDate, endDate);        
        });
        this.chart.on('click', params => {
          if (params.componentSubType === 'custom') {
            if (params.value.length === 4) {
              this.$router.push({
                name: 'event-detail',
                query: {
                  name: params.value[2],
                  id: params.value[3]
                }
              });
            }
          }
        });
        this.chart.getZr().on('mousemove', param => {
          const pointInPixel = [param.offsetX, param.offsetY];
          if (this.chart.containPixel('grid', pointInPixel)) {
            this.chart.getZr().setCursorStyle('pointer');
          } else {
            this.chart.getZr().setCursorStyle('default');
          }
        });
        this.chart.getZr().on('click', params => {
          const pointInPixel = [params.offsetX, params.offsetY];
          if (this.chart.containPixel('grid', pointInPixel)) {
            this.$emit('checkedDate', this.indexArrData);
          }
        });

        this.chart.on('legendselectchanged', evt => {
          if (evt.name === '公司热度') {
            this.chart.dispatchAction({
              type: 'legendSelect',
              name: evt.name
            });
          }
        });
      } else {
        const chartSeries = this.getSeriesData(this.dateRangeArr, chartData);
        const chartOption = this.chart.getOption();
        chartOption.series = chartSeries;
        this.chart.setOption(chartOption, {
          notMerge: true
        });
      }      
    }
  },
  created() {
    this.dateRangeArr = this.getXAxisData();
    // this.dateRangeArr = this.dateRangeArr.slice(this.dateRangeArr.indexOf('2020-01-01'), this.dateRangeArr.length);
    QuestionQuery.getSentimentTrends(this.searchText, this.dateRangeArr[0], this.dateRangeArr[this.dateRangeArr.length - 1]).then(data => {
      this.chartData = data; 
      this.$emit('done');    
      this.renderChart(this.chartData);
    }, () => this.$emit('done'));
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.off('dataZoom');
      this.chart.off('click');
      this.chart.off('legendselectchanged');
      this.chart.dispose();
    }
  },
  components: {

  }
};
</script>
<style lang="scss" scoped>
.sentiment-trends-chart {
  padding-bottom: 16px;
  width: 100%;
  height: 340px;
  background-color: #ffffff;
  .chart {
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
}
</style>

